/*
 * <tree-truck-tracker> — public "where's the truck" panel for both brands.
 *
 * Shows where the truck is today (by neighborhood, never by address), where
 * it's headed next, where it's booked this week, and lets someone nearby ask
 * for a same-day stop with a tree off the truck, paid at the door.
 *
 * Usage (load this file with a script tag first):
 *   <tree-truck-tracker api-base="https://scheduler.example.com" brand="comets"
 *                       booking-url="/cart"></tree-truck-tracker>
 *
 * Attributes
 *   api-base     URL of the scheduling service
 *   brand        recorded on flag-down stops
 *   booking-url  where "book a delivery" links go (the page with the delivery picker)
 *   refresh      seconds between live updates (default 90)
 *
 * Address lookup: same hooks as <tree-delivery-picker>
 *   tracker.suggest = async (text) => [{ label, ... }]
 *   tracker.resolve = async (suggestion) => ({ label, lat, lng })
 *
 * Event: `truck-stop-requested` with { id, start, end, fee, address } after a flag-down.
 * Theme with the same --tdw-* CSS variables as the delivery picker.
 */
(() => {
  const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const BLOCKS = { morning: "Morning", afternoon: "Afternoon" };
  const pad = (n) => String(n).padStart(2, "0");
  const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const money = (n) => `$${Number.isInteger(n) ? n : Number(n).toFixed(2)}`;
  const parseDate = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
  function clock(hhmm) {
    const [h, m] = hhmm.split(":").map(Number);
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${m ? `${h12}:${pad(m)}` : h12} ${h < 12 ? "am" : "pm"}`;
  }
  const span = (a, b) => `${clock(a)} to ${clock(b)}`;
  function dayName(iso, todayIso) {
    if (iso === todayIso) return "Today";
    const d = parseDate(iso), t = parseDate(todayIso);
    if ((d - t) / 864e5 === 1) return "Tomorrow";
    return `${DOW[d.getDay()]}, ${MON[d.getMonth()]} ${d.getDate()}`;
  }
  const TREE = `<svg class="tree" viewBox="0 0 12 14" aria-hidden="true"><path d="M6 0 1.5 6h2.2L.8 10h4.4v4h1.6v-4h4.4L8.3 6h2.2z"/></svg>`;

  class HttpApi {
    constructor(base) { this.base = base.replace(/\/$/, ""); }
    async _send(method, path, body) {
      const res = await fetch(this.base + path, {
        method, headers: body ? { "Content-Type": "application/json" } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { const e = new Error(data.detail || res.statusText); e.status = res.status; throw e; }
      return data;
    }
    truck() { return this._send("GET", "/public/truck"); }
    stopOptions(q) { return this._send("POST", "/public/stop-for-me/options", q); }
    requestStop(b) { return this._send("POST", "/public/stop-for-me", b); }
  }

  const STYLES = `
    :host {
      --_font: var(--tdw-font, inherit); --_ink: var(--tdw-ink, #1c2a22); --_muted: var(--tdw-muted, #5a6a60);
      --_bg: var(--tdw-bg, #ffffff); --_surface: var(--tdw-surface, #f2f5f1); --_line: var(--tdw-line, #d3dcd5);
      --_accent: var(--tdw-accent, #1f4d36); --_accent-ink: var(--tdw-accent-ink, #ffffff);
      --_nearby: var(--tdw-nearby, #2d7650); --_nearby-bg: var(--tdw-nearby-bg, #e4f1e8);
      --_radius: var(--tdw-radius, 10px); --_error: var(--tdw-error, #a3261c);
      display: block; font-family: var(--_font); color: var(--_ink);
    }
    * { box-sizing: border-box; }
    .wrap { background: var(--_bg); max-width: 40rem; line-height: 1.45; }
    p { margin: 0; }
    h3 { font-size: 1.05rem; font-weight: 650; margin: 0 0 .6rem; }
    section + section { margin-top: 1.8rem; }
    .muted { color: var(--_muted); font-size: .92rem; }
    button { font: inherit; color: inherit; cursor: pointer; }
    :focus-visible { outline: 2px solid var(--_accent); outline-offset: 2px; }
    .tree { width: .75rem; height: .9rem; fill: currentColor; flex: none; }

    /* Live */
    .status { display: flex; align-items: center; gap: .45rem; color: var(--_muted); font-size: .92rem; }
    .dot { width: .6rem; height: .6rem; border-radius: 50%; background: var(--_line); flex: none; }
    .dot.live { background: var(--_nearby); box-shadow: 0 0 0 0 var(--_nearby); animation: pulse 2.4s ease-out infinite; }
    @keyframes pulse { 0% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--_nearby) 45%, transparent); } 70%, 100% { box-shadow: 0 0 0 .55rem transparent; } }
    @media (prefers-reduced-motion: reduce) { .dot.live { animation: none; } }
    .where { font-size: clamp(1.9rem, 6vw, 2.6rem); font-weight: 700; line-height: 1.1; margin: .2rem 0 .1rem; letter-spacing: -.01em; }
    .route { list-style: none; margin: 1rem 0 0; padding: 0; border-left: 2px solid var(--_line); }
    .route li { position: relative; padding: .15rem 0 .65rem 1rem; display: grid; grid-template-columns: 10.5rem 1fr; gap: .2rem 1rem; }
    .route li::before { content: ""; position: absolute; left: -.38rem; top: .5rem; width: .6rem; height: .6rem; border-radius: 50%; background: var(--_bg); border: 2px solid var(--_nearby); }
    .route .when { color: var(--_muted); font-size: .92rem; font-variant-numeric: tabular-nums; }
    .route .area { font-weight: 600; }
    @media (max-width: 26rem) { .route li { grid-template-columns: 1fr; gap: 0; } }

    /* Flag-down */
    .flag { background: var(--_nearby-bg); border-radius: var(--_radius); padding: 1.1rem 1.15rem; }
    .flag h3 { display: flex; align-items: center; gap: .45rem; color: var(--_nearby); }
    .flag p + p { margin-top: .4rem; }
    .primary { display: inline-block; margin-top: .9rem; padding: .75rem 1.1rem; border: 0; border-radius: var(--_radius);
      background: var(--_accent); color: var(--_accent-ink); font-weight: 650; text-decoration: none; }
    .primary:disabled { opacity: .45; cursor: default; }
    .link { background: none; border: 0; padding: 0; color: var(--_accent); text-decoration: underline; text-underline-offset: 2px; }
    .addr { position: relative; margin-top: .8rem; }
    input { width: 100%; font: inherit; font-size: 1rem; color: var(--_ink); background: var(--_bg); padding: .75rem .85rem;
      border: 1.5px solid var(--_line); border-radius: var(--_radius); }
    input:focus { border-color: var(--_accent); outline: none; }
    .suggestions { position: absolute; z-index: 5; left: 0; right: 0; top: calc(100% + 4px); margin: 0; padding: .3rem; list-style: none;
      background: var(--_bg); border: 1px solid var(--_line); border-radius: var(--_radius); box-shadow: 0 8px 24px rgba(20, 40, 30, .12); }
    .suggestions li { padding: .55rem .6rem; border-radius: 6px; cursor: pointer; }
    .suggestions li[aria-selected="true"], .suggestions li:hover { background: var(--_surface); }
    .times { display: flex; flex-wrap: wrap; gap: .4rem; margin: .8rem 0 .2rem; }
    .time { padding: .55rem .8rem; border: 1.5px solid var(--_line); border-radius: var(--_radius); background: var(--_bg); }
    .time[aria-pressed="true"] { background: var(--_accent); border-color: var(--_accent); color: var(--_accent-ink); }
    .fields { display: grid; grid-template-columns: 1fr 1fr; gap: .5rem; margin-top: .6rem; }
    @media (max-width: 26rem) { .fields { grid-template-columns: 1fr; } }
    label { display: grid; gap: .25rem; font-size: .9rem; }
    .error { color: var(--_error); font-size: .92rem; margin-top: .6rem; }

    /* Week */
    .week { display: grid; gap: 0; }
    .day { display: grid; grid-template-columns: minmax(8rem, 10rem) 1fr; gap: .2rem 1rem; padding: .7rem 0; border-top: 1px solid var(--_line); }
    .day:last-child { border-bottom: 1px solid var(--_line); }
    .day .name { font-weight: 650; }
    .day .blocks { display: grid; gap: .15rem; }
    .day .blocks span { color: var(--_muted); }
    @media (max-width: 26rem) { .day { grid-template-columns: 1fr; } }
    .save { display: flex; align-items: center; gap: .45rem; margin-top: .8rem; color: var(--_nearby); font-size: .92rem; }
    .save a { color: var(--_accent); }
  `;

  class TreeTruckTracker extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" });
      this.suggest = null; this.resolve = null; this.api = null;
      this.data = null;
      this.flow = { step: "idle" };   // idle | address | loading | pick | sending | done | none
      this._sugg = []; this._active = -1;
    }

    connectedCallback() {
      if (this._built) return;
      this._built = true;
      this.shadowRoot.innerHTML = `<style>${STYLES}</style>
        <div class="wrap" part="root">
          <section class="live" aria-live="polite"><p class="muted">Checking where the truck is…</p></section>
          <section class="flagdown" hidden></section>
          <section class="weekly"></section>
        </div>`;
      this.$live = this.shadowRoot.querySelector(".live");
      this.$flag = this.shadowRoot.querySelector(".flagdown");
      this.$week = this.shadowRoot.querySelector(".weekly");
      this.$flag.addEventListener("click", (e) => this._onFlagClick(e));
      this.$flag.addEventListener("submit", (e) => this._onSubmit(e));
      this.$flag.addEventListener("input", (e) => { if (e.target.name === "address") this._onType(e.target); });
      this.$flag.addEventListener("keydown", (e) => { if (e.target.name === "address") this._onKey(e); });
      // Wait until the page has finished running its scripts, so it can set
      // api/suggest/resolve before the first fetch.
      const first = () => setTimeout(() => this._load(), 0);
      if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", first, { once: true });
      else first();
      const every = Number(this.getAttribute("refresh") || 90) * 1000;
      this._timer = setInterval(() => this._load(), every);
      this._vis = () => { if (!document.hidden) this._load(); };
      document.addEventListener("visibilitychange", this._vis);
    }

    disconnectedCallback() { clearInterval(this._timer); document.removeEventListener("visibilitychange", this._vis); }

    get _client() { return this.api || (this._http ||= new HttpApi(this.getAttribute("api-base") || "")); }
    get _bookingUrl() { return this.getAttribute("booking-url") || ""; }

    async _load() {
      try { this.data = await this._client.truck(); }
      catch { if (!this.data) this.$live.innerHTML = `<p class="muted">The truck's location isn't available right now.</p>`; return; }
      this._renderLive(); this._renderWeek();
      if (this.flow.step === "idle") this._renderFlag();
    }

    // ------------------------------------------------------------- live
    _renderLive() {
      const t = this.data.today;
      const route = t.coming_up.length ? `<ol class="route">${t.coming_up.map((c) =>
        `<li><span class="when">${span(c.from, c.to)}</span><span class="area">${esc(c.area)}</span></li>`).join("")}</ol>` : "";
      let html;
      if (t.status === "on_the_road") {
        html = `<p class="status"><span class="dot live"></span>On the road now</p>
          <p class="where">${esc(t.current)}</p>
          ${route ? `<p class="muted" style="margin-top:1rem">Coming up today</p>${route}` : ""}`;
      } else if (t.status === "not_started") {
        html = `<p class="status"><span class="dot"></span>Heading out at ${clock(t.leaves_at)}</p>
          <p class="where">Today's route</p>${route}`;
      } else if (t.status === "done") {
        html = `<p class="status"><span class="dot"></span>Done for today</p>
          <p class="where">Back at the yard</p>`;
      } else {
        html = `<p class="status"><span class="dot"></span>No deliveries today</p>
          <p class="where">The truck is at the yard</p>`;
      }
      this.$live.innerHTML = html;
    }

    // ------------------------------------------------------------- week
    _renderWeek() {
      const { week, today, neighborhood_discount: off } = this.data;
      const upcoming = week.filter((d) => d.date !== today.date);
      if (!upcoming.length) { this.$week.innerHTML = ""; return; }
      const url = this._bookingUrl;
      this.$week.innerHTML = `<h3>Where we'll be this week</h3>
        <div class="week">${upcoming.map((d) => `<div class="day">
          <span class="name">${dayName(d.date, today.date)}</span>
          <span class="blocks">${d.blocks.map((b) => `<span>${BLOCKS[b.block] || b.block}: ${b.areas.map(esc).join(", ")}</span>`).join("")}</span>
        </div>`).join("")}</div>
        <p class="save">${TREE}<span>Book a delivery for when our truck is already in your neighborhood${off > 0 ? ` and save ${money(off)}` : ""}.${url ? ` <a href="${esc(url)}">Book a delivery</a>` : ""}</span></p>`;
    }

    // --------------------------------------------------------- flag-down
    _renderFlag() {
      const d = this.data;
      const f = this.flow;
      if (!d || (!d.flagdown_open && !["pick", "sending", "done", "none"].includes(f.step))) {
        this.$flag.hidden = true; this.$flag.innerHTML = ""; return;
      }
      this.$flag.hidden = false;
      const fee = money(d.flagdown_fee);
      let body = "";
      if (f.step === "idle") {
        const pay = d.flagdown_fee > 0 ? `Delivery is ${fee}.` : "Same price as online, delivery included.";
        body = `<p>We carry spare trees. If you're near today's route, we can stop by: pick a tree at your door and pay the driver when we arrive. ${pay}</p>
          <button class="primary" data-act="start">Stop for me</button>`;
      } else if (f.step === "address" || f.step === "loading") {
        body = `<p>Where should we stop?</p>
          <div class="addr"><input name="address" type="text" autocomplete="street-address" placeholder="Street address"
            role="combobox" aria-autocomplete="list" aria-expanded="false" aria-controls="fsugg" aria-label="Street address">
            <ul class="suggestions" id="fsugg" role="listbox" hidden></ul></div>
          ${f.step === "loading" ? `<p class="muted" style="margin-top:.6rem">Checking today's route…</p>` : ""}
          ${f.error ? `<p class="error">${esc(f.error)}</p>` : ""}
          <p style="margin-top:.8rem"><button class="link" data-act="cancel">Cancel</button></p>`;
      } else if (f.step === "pick" || f.step === "sending") {
        body = `<p>We can stop at <strong>${esc(f.address.label)}</strong> today. When works?</p>
          <form novalidate>
            <div class="times" role="group" aria-label="Stop times">${f.options.map((o) =>
              `<button type="button" class="time" data-act="time" data-v="${o.start}" aria-pressed="${o.start === f.start}">${span(o.start, o.end)}</button>`).join("")}</div>
            <div class="fields">
              <label>Name<input name="fullname" autocomplete="name" required></label>
              <label>Phone<input name="phone" type="tel" autocomplete="tel" required></label>
            </div>
            <button class="primary" type="submit" ${f.start && f.step !== "sending" ? "" : "disabled"}>${f.step === "sending" ? "Sending…" : "Ask for a stop"}</button>
          </form>
          ${f.error ? `<p class="error">${esc(f.error)}</p>` : ""}
          <p style="margin-top:.8rem"><button class="link" data-act="cancel">Cancel</button></p>`;
      } else if (f.step === "done") {
        body = `<p><strong>We'll stop by between ${clock(f.booked.start)} and ${clock(f.booked.end)}.</strong></p>
          <p>Pick your tree from the truck and pay the driver when we arrive. We'll call if we're running late.</p>`;
      } else if (f.step === "none") {
        const url = this._bookingUrl;
        body = `<p>We can't fit a stop at that address into today's route.</p>
          <p>${url ? `<a class="link" href="${esc(url)}">Book a delivery</a> for when we're in your area, and save on delivery.` : "Check the schedule below for when we'll be in your area."}</p>
          <p style="margin-top:.8rem"><button class="link" data-act="start">Try another address</button></p>`;
      }
      this.$flag.innerHTML = `<div class="flag"><h3>${TREE}Near the truck today?</h3>${body}</div>`;
    }

    _setFlow(next) {
      const keep = this.$flag.querySelector("form");
      const saved = keep ? { name: keep.elements.fullname?.value, phone: keep.elements.phone?.value } : null;
      this.flow = { ...this.flow, ...next };
      this._renderFlag();
      const form = this.$flag.querySelector("form");
      if (form && saved) { form.elements.fullname.value = saved.name || ""; form.elements.phone.value = saved.phone || ""; }
    }

    _onFlagClick(e) {
      const li = e.target.closest(".suggestions li");
      if (li) return this._pick(Number(li.dataset.i));
      const el = e.target.closest("[data-act]");
      if (!el) return;
      const act = el.dataset.act;
      if (act === "start") {
        this._setFlow({ step: "address", error: "", start: null });
        requestAnimationFrame(() => this.$flag.querySelector("input[name=address]")?.focus());
      }
      if (act === "cancel") { this.flow = { step: "idle" }; this._renderFlag(); }
      if (act === "time") this._setFlow({ start: el.dataset.v });
    }

    _onType(input) {
      clearTimeout(this._deb);
      const text = input.value.trim();
      if (!this.suggest || text.length < 3) return this._showSugg([]);
      this._deb = setTimeout(async () => { try { this._showSugg(await this.suggest(text)); } catch { this._showSugg([]); } }, 200);
    }

    _showSugg(items) {
      this._sugg = items || []; this._active = -1;
      const list = this.$flag.querySelector(".suggestions"), input = this.$flag.querySelector("input[name=address]");
      if (!list) return;
      list.innerHTML = this._sugg.map((s, i) => `<li role="option" id="fo-${i}" data-i="${i}" aria-selected="false">${esc(s.label)}</li>`).join("");
      list.hidden = !this._sugg.length;
      input.setAttribute("aria-expanded", String(!!this._sugg.length));
    }

    _onKey(e) {
      const n = this._sugg.length;
      if (!n) return;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        this._active = (this._active + (e.key === "ArrowDown" ? 1 : -1) + n) % n;
        [...this.$flag.querySelectorAll(".suggestions li")].forEach((li, i) => li.setAttribute("aria-selected", String(i === this._active)));
        e.target.setAttribute("aria-activedescendant", `fo-${this._active}`);
      } else if (e.key === "Enter") { e.preventDefault(); this._pick(this._active >= 0 ? this._active : 0); }
      else if (e.key === "Escape") this._showSugg([]);
    }

    async _pick(i) {
      const choice = this._sugg[i];
      this._showSugg([]);
      if (!choice) return;
      this._setFlow({ step: "loading", error: "" });
      try {
        const addr = this.resolve ? await this.resolve(choice) : choice;
        const res = await this._client.stopOptions({ lat: addr.lat, lng: addr.lng });
        const address = { label: addr.label || choice.label, lat: addr.lat, lng: addr.lng };
        if (!res.options.length) return this._setFlow({ step: "none", address });
        this._setFlow({ step: "pick", address, options: res.options, start: res.options[0].start });
      } catch {
        this._setFlow({ step: "address", error: "We couldn't check that address. Try again in a moment." });
      }
    }

    async _onSubmit(e) {
      e.preventDefault();
      const form = e.target, f = this.flow;
      const name = form.elements.fullname.value.trim(), phone = form.elements.phone.value.trim();
      if (!name || !phone) return this._setFlow({ error: "Add your name and a phone number so the driver can reach you." });
      this._setFlow({ step: "sending", error: "" });
      try {
        const b = await this._client.requestStop({ lat: f.address.lat, lng: f.address.lng, start: f.start,
          brand: this.getAttribute("brand") || "", address: f.address.label, name, phone });
        this._setFlow({ step: "done", booked: b });
        this.dispatchEvent(new CustomEvent("truck-stop-requested", { bubbles: true, composed: true,
          detail: { ...b, address: f.address.label } }));
        this._load();
      } catch (err) {
        if (err.status === 409) {
          const res = await this._client.stopOptions({ lat: f.address.lat, lng: f.address.lng }).catch(() => ({ options: [] }));
          if (!res.options.length) return this._setFlow({ step: "none" });
          this._setFlow({ step: "pick", options: res.options, start: res.options[0].start,
            error: "That time just filled up. Here's what's still open." });
        } else {
          this._setFlow({ step: "pick", error: "Your request didn't go through. Try again in a moment." });
        }
      }
    }
  }

  if (!customElements.get("tree-truck-tracker")) customElements.define("tree-truck-tracker", TreeTruckTracker);
})();